import InfoIcon from '@mui/icons-material/Info'
import { FormControl, Select, MenuItem } from '@mui/material'
import { Controller, useFormContext } from 'react-hook-form'
import { z } from 'zod'
import {
  CampaignCategory,
  FormatVersion,
  ReviewSection,
  ReviewTarget,
  SearchGenre,
  CampaignCostHandlingMethod,
} from '~/generated/graphql'
import { CampaignInputComponent, CampaignInputComponentProps } from '~/features/campaign_input/CampaignInputComponent'
import { UnsetAlert } from '~/components/common/UnsetAlert'
import { useEffect, useRef } from 'react'
import { categoryField } from './type'
import { ValidationErrorMessage } from '~/components/common/ValidationErrorMessage'
import { getMaxCompanionCountTitle } from '~/lib/feature/getCampaignTitle'

export const maxCompanionCountSchemaFields = {
  maxCompanionCount: z.number().refine((value) => value <= 3, { message: '同伴者は3人までで設定してください' }),
  searchGenre: z.nativeEnum(SearchGenre).or(z.literal('')),
  costHandlingMethod: z.nativeEnum(CampaignCostHandlingMethod),
  ...categoryField,
}

export const maxCompanionCountSchema = z.object(maxCompanionCountSchemaFields)
type Props = CampaignInputComponentProps

export const MaxCompanionCount = ({ description, tooltip }: Props) => {
  const { watch, setValue, control, getValues } = useFormContext<z.infer<typeof maxCompanionCountSchema>>()

  const category = watch('category')

  const searchGenre = watch('searchGenre')
  const options = [0, 1, 2, 3]
  const costHandlingMethod = getValues('costHandlingMethod')

  const isFirstUpdate = useRef(true)

  // ジャンル変更時に maxCompanionCount のデフォルト値を更新
  useEffect(() => {
    // 初回のレンダリング時は何もしない（キャンペーンコピーの時にsetValueを実行したくないため）
    if (isFirstUpdate.current) {
      isFirstUpdate.current = false
      return
    }

    if (!searchGenre) return
    if (category === CampaignCategory.Product) return
    setValue('maxCompanionCount', 0)
  }, [searchGenre, setValue, category])

  if (category === CampaignCategory.Product) {
    return
  }
  return (
    <CampaignInputComponent
      title={getMaxCompanionCountTitle(costHandlingMethod)}
      required={true}
      description={description}
      tooltip={tooltip}
      review={{
        section: ReviewSection.Campaign,
        targets: [ReviewTarget.MaxCompanionCount],
      }}
      compatibleFormatVersions={[FormatVersion.V4Campaign]}
    >
      {searchGenre ? (
        <Controller
          name="maxCompanionCount"
          control={control}
          defaultValue={0}
          render={({ field, fieldState: { error } }) => (
            <FormControl>
              <Select id="maxCompanionCount" {...field} inputRef={field.ref} error={!!error}>
                {options.map((value) => (
                  <MenuItem key={value} value={value}>
                    {value === 0 ? '同伴者なし' : `${value}人`}
                  </MenuItem>
                ))}
              </Select>
              <ValidationErrorMessage error={error?.message} />
            </FormControl>
          )}
        />
      ) : (
        <UnsetAlert icon={<InfoIcon color="action" />}>ジャンルを選択すると、入力項目が表示されます</UnsetAlert>
      )}
    </CampaignInputComponent>
  )
}
